import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { format, parse, lastDayOfMonth as lastDayOfMonthFn, getDate, isToday as isTodayFn } from 'date-fns';
import { useAppSelector } from '@/redux/hooks';
import { selectCurrentTableData } from '@/redux/selectors/dataTableSelectors';
import { useQuery } from '@/components/_hooks/useQuery';
import { CALENDAR_START_DATE_FORMAT, getMonthsFirstDay, getMostRecentSunday, getUpcomingSaturday, parseStartDateQueryParam } from '@/_utils/date';
import Cell from '@/components/_common/Table/Calendar/CalendarSharedComponents/Cell';
import CellHeader from '@/components/_common/Table/Calendar/CalendarSharedComponents/CellHeader';
import { ApiHourlyData } from '@/components/_common/Table/Calendar/CalendarComponents/types';
import { getAvailStatus, getColorByAvailStatus, groupAvailsByDate } from '@/components/_common/Table/Calendar/utils';
import AvailDetailsPopover from '@/components/_common/Table/Calendar/CalendarComponents/AvailDetailsPopover';
import { Bullet, GridViewAvail } from '@/components/_common/Table/Calendar/CalendarComponents/common';
import ShowMore from '@/components/_common/Table/Calendar/CalendarSharedComponents/ShowMore';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import { FOCUS_CALENDAR_MONTHLY_VIEW_ITEMS_PER_DAY } from '@/constants/constants';
import { useFocusTheme } from '@/components/Focus/configs/useFocusTheme.hook';

interface Props {
  calendarContainer: HTMLElement | null;
  totalCountByDay: Record<string, number> | undefined;
}

const MonthlyView: React.FC<Props> = ({ calendarContainer, totalCountByDay }) => {
  const theme = useFocusTheme();
  const query = useQuery();
  const calendarStartDateParam = query.get('calendarStartDate');
  const apiData: ApiHourlyData[] = useAppSelector(selectCurrentTableData);
  const { time_format } = useAppSelector(selectUserPreferences);

  const itemsByDate = useMemo((): Record<string, ApiHourlyData[]> => {
    if (!calendarStartDateParam) return {};

    const parsedDate = parseStartDateQueryParam(calendarStartDateParam);
    const firstDayOfMonth = getMonthsFirstDay(parsedDate);
    const lastDayOfMonth = lastDayOfMonthFn(parsedDate);
    // define the first and the last date that will be displayed in the calendar
    const start = getMostRecentSunday(firstDayOfMonth);
    const end = getUpcomingSaturday(lastDayOfMonth);
    return groupAvailsByDate(start, end, apiData);
  }, [calendarStartDateParam, apiData]);

  return Object.entries(itemsByDate).map(([date, avails]) => {
    const dateParsed = parse(date, CALENDAR_START_DATE_FORMAT, new Date());
    const isToday = isTodayFn(dateParsed);
    const isFirstDayOfMonth = getDate(dateParsed) === 1;
    const dateLabel = isFirstDayOfMonth ? format(dateParsed, 'MMM d') : format(dateParsed, 'd');

    return (
      <Cell key={date} isHighlighted={isToday}>
        <CellHeader label={dateLabel} isHighlighted={isFirstDayOfMonth} isEmphasized={isToday} />

        <ShowMore date={date} limit={FOCUS_CALENDAR_MONTHLY_VIEW_ITEMS_PER_DAY} totalCount={totalCountByDay?.[date]} avails={avails}>
          {(avail) => {
            const time = format(new Date(avail.term_start), time_format || 'hh:mm aaaaa');
            const status = getAvailStatus(avail);
            const color = getColorByAvailStatus(status, theme.palette.mode === 'dark');

            return (
              <AvailDetailsPopover key={avail.id} availId={avail.id} calendarContainer={calendarContainer}>
                <GridViewAvail availId={avail.id} status={status}>
                  <Grid item>
                    <Bullet sx={{ backgroundColor: color }} />
                  </Grid>
                  <Grid item sx={{ flexGrow: 1, fontSize: '.857rem', lineHeight: '1em' }}>
                    {avail.title.client_ids_supplemental.alpha_id}
                  </Grid>
                  <Grid item sx={{ textTransform: 'uppercase', fontSize: '0.714rem', fontWeight: 700 }}>
                    {time}
                  </Grid>
                </GridViewAvail>
              </AvailDetailsPopover>
            );
          }}
        </ShowMore>
      </Cell>
    );
  });
};

export default MonthlyView;
